import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import dayjs from 'dayjs'
import DateTimePicker, { DateTimePickerProps } from 'react-datetime-picker'
import cx from 'classnames'
import 'react-datetime-picker/dist/DateTimePicker.css'
import 'react-calendar/dist/Calendar.css'
import 'react-clock/dist/Clock.css'
import DatePickerUtils, {
  DATE_PICKER_CAL_FIXED_CN,
  DATE_PICKER_CN,
  DATE_PICKER_SIZE_MD_CN,
  DATE_PICKER_SIZE_LG_CN,
  DATE_PICKER_TILE_CN,
  DATE_PICKER_V2_CN,
  DATE_PICKER_CAL_V2_CN,
} from './DatePickerUtils'
import './datePickerStyles.css'
import './datePickerStylesV2.css'

export type { DatePickerValue, VariableDatePickerValue } from './DatePickerInput'

export interface DatePickerInputV2Props extends Omit<DateTimePickerProps, 'value' | 'onChange' | 'size'> {
  size?: 'sm' | 'md' | 'lg'
  showAmPm?: boolean
  showTimeSelect?: boolean
  fixedCalendar?: boolean
  placement?: 'top' | 'bottom'
  value?: Date | null | string | undefined
  onChange?: (v: Date | null) => void
  onReset?: () => void
}

const uuid = () => '_' + Math.random().toString(36).substring(2, 11)

export const DatePickerInputV2 = forwardRef<HTMLDivElement, DatePickerInputV2Props>(
  (
    {
      isCalendarOpen,
      showTimeSelect,
      showAmPm,
      value,
      onChange,
      onReset,
      size,
      fixedCalendar,
      placement = 'bottom',
      className,
      calendarClassName,
      disabled = false,
      ...rest
    },
    ref,
  ) => {
    const id = useMemo(() => uuid(), [])
    const calCn = useMemo(() => `${id}__calendar`, [id])

    const [temp, setTemp] = useState<Date | null>(null)
    const [calendarElement, setCalendarElement] = useState<Element | null>(null)
    const formattedValue = useMemo<Date | null>(() => DatePickerUtils.getValidDate(value), [value])

    const defaultOpts = useMemo(
      () => ({
        ...DatePickerUtils.getDatePickerDefaultOpts({ showTimeSelect, showAmPm, fixedCalendar }),
        formatMonthYear: (_locale: string | undefined, date: Date) => dayjs(date).format('YYYY년 M월'),
      }),
      [showTimeSelect, showAmPm, fixedCalendar],
    )

    const handleChange = useCallback(
      (v: Date | null) => {
        if (!onChange) return
        if (!dayjs(v).isValid()) return

        setTemp(v)

        const $active = document.activeElement as HTMLElement
        if ($active && $active.tagName === 'BUTTON' && $active.classList.contains(DATE_PICKER_TILE_CN)) {
          onChange(v)
        }
      },
      [onChange],
    )

    const onCalendarOpen = useCallback(() => {
      if (!fixedCalendar || disabled) return
      DatePickerUtils.onFixPositionOfCal({ id, size, calCn, placement })
      if (onReset) {
        setTimeout(() => {
          const calElem = document.querySelector(`.${calCn}`)
          if (calElem) setCalendarElement(calElem)
        }, 0)
      }
    }, [id, size, fixedCalendar, calCn, disabled, placement, onReset])

    const onCalendarClose = useCallback(() => {
      setCalendarElement(null)
    }, [])

    const onClickInput = useCallback(() => {
      if (disabled) return
      DatePickerUtils.onForceToOpen('simple', calCn, onCalendarOpen)
      DatePickerUtils.onSelectAllOnDateInput()
    }, [calCn, disabled, onCalendarOpen])

    useEffect(
      function onInitTemp() {
        if (temp === null && formattedValue) {
          setTemp(formattedValue)
        } else if (temp && !formattedValue) {
          setTemp(null)
        }
      },
      [formattedValue],
    )

    useEffect(
      function onMount() {
        if (isCalendarOpen) {
          onCalendarOpen()
        }

        const onCloseCal = () => DatePickerUtils.onForceToClose('simple', calCn)
        window.addEventListener('scroll', onCloseCal)
        return () => window.removeEventListener('scroll', onCloseCal)
      },
      [calCn],
    )

    return (
      <>
        <DateTimePicker
          {...defaultOpts}
          inputRef={ref}
          isCalendarOpen={isCalendarOpen}
          id={id}
          value={formattedValue}
          className={cx(
            DATE_PICKER_CN,
            DATE_PICKER_V2_CN,
            {
              [DATE_PICKER_SIZE_MD_CN]: size === 'md',
              [DATE_PICKER_SIZE_LG_CN]: size === 'lg',
            },
            className,
          )}
          calendarClassName={cx(DATE_PICKER_CAL_V2_CN, calCn, calendarClassName, { [DATE_PICKER_CAL_FIXED_CN]: fixedCalendar })}
          tileClassName={DATE_PICKER_TILE_CN}
          onChange={handleChange}
          onClick={onClickInput}
          onCalendarOpen={onCalendarOpen}
          onCalendarClose={onCalendarClose}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key !== 'Tab') e.preventDefault()
          }}
          onBlur={(e) => {
            const $target = e.target as HTMLInputElement
            if ($target && $target.tagName === 'INPUT' && onChange) onChange(temp)
          }}
          disabled={disabled}
          {...rest}
        />
        {calendarElement &&
          onReset &&
          createPortal(
            <div className="mwms-calendar__footer">
              <button
                type="button"
                className="mwms-calendar__reset-btn"
                onMouseDown={(e) => {
                  e.preventDefault()
                  onChange?.(null)
                  onReset()
                  // calCn은 inner react-calendar에 붙어있으므로 parentElement(outer wrapper)의 클래스를 직접 토글
                  const $calInner = document.querySelector(`.${calCn}`) as HTMLElement
                  const $calWrapper = $calInner?.parentElement
                  if ($calWrapper) {
                    $calWrapper.classList.remove('react-datetime-picker__calendar--open')
                    $calWrapper.classList.add('react-datetime-picker__calendar--closed')
                  }
                  setCalendarElement(null)
                }}
              >
                초기화
              </button>
            </div>,
            calendarElement,
          )}
      </>
    )
  },
)
